import { ChangeDetectionStrategy, Component, signal } from '@angular/core'
import { months } from '../incident-form/incident.definitions'
import {
    FormBuilder,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms'
import { MatDialogRef } from '@angular/material/dialog'
import { ExportIncident } from '../../../models/incident.interface'
import { QueryGet } from '../../../shared/query-types'
import { IncidentService } from '../../../services/incident/incident.service'
import { AxiosResponse } from 'axios'
import { ToastrService } from 'ngx-toastr'
import { HttpErrorResponse } from '@angular/common/http'
import saveAs from 'file-saver'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatChipsModule } from '@angular/material/chips'
import {
    CdkDrag,
    CdkDragDrop,
    CdkDropList,
    DragDropModule,
    moveItemInArray,
} from '@angular/cdk/drag-drop'
import {
    MatDatepicker,
    MatDatepickerModule,
    MatDatepickerToggle,
} from '@angular/material/datepicker'
import { MatInputModule } from '@angular/material/input'
import { DatePipe, NgIf } from '@angular/common'
import { LoadingService } from '../../../services/loadingService/loading.service'
import { DateAdapter } from '@angular/material/core'
import { FormUtils } from '../../../utils'

interface IncidentField {
    key: string
    label: string
}

@Component({
    selector: 'app-export-incident',
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatError,
        MatIcon,
        MatChipsModule,
        CdkDropList,
        CdkDrag,
        MatDatepickerModule,
        MatDatepicker,
        MatDatepickerToggle,
        MatInputModule,
        NgIf,
        DragDropModule,
    ],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './export-incident.component.html',
    styleUrl: './export-incident.component.css',
})
export class ExportIncidentComponent {
    errorMsg: string[] | null = null
    months = months
    currentYears: number[] = []

    readonly fields = signal<IncidentField[]>([
        { key: 'numero_caso', label: 'Código del caso' },
        { key: 'violencia', label: 'Violencia' },
        { key: 'tipo_violencia', label: 'Tipo de violencia' },
        { key: 'relacion_conflicto', label: 'Relación con el conflicto' },
        // { key: 'agresion_fisica', label: 'Agresión física' },
        { key: 'tipo_afectacion', label: 'Tipo de afectación' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'relacion_testigo', label: 'Relación con el testigo' },
        { key: 'municipio', label: 'Municipio' },
        { key: 'direccion', label: 'Dirección' },
        { key: 'pais', label: 'País' },
        { key: 'departamento', label: 'Departamento' },
        { key: 'ciudad', label: 'Ciudad' },
        { key: 'denuncio', label: 'Denuncio' },
        { key: 'porque_no_denuncio', label: 'Por qué no denunció' },
        { key: 'denuncio_ante_quien', label: 'Denuncio ante quién' },
        { key: 'medidas_tomadas', label: 'Medidas tomadas' },
        { key: 'fecha_denuncia', label: 'Fecha de denuncia' },
        { key: 'victimas', label: 'Víctimas' },
        { key: 'fecha_nacimiento', label: 'Fecha de nacimiento' },
        { key: 'edad', label: 'Edad' },
        { key: 'lugar_nacimiento', label: 'Lugar de nacimiento' },
        { key: 'estado_civil', label: 'Estado civil' },
        { key: 'genero', label: 'Género' },
        { key: 'telefono', label: 'Teléfono' },
        { key: 'correo', label: 'Correo electrónico' },
        { key: 'territorio', label: 'Territorio' },
        { key: 'nivel_educativo', label: 'Nivel educativo' },
        { key: 'educacion_propia', label: 'Educación propia' },
        { key: 'habla_nasa_yuwe', label: 'Habla nasayuwe' },
        { key: 'analfabeta', label: 'Es analfabeta' },
        { key: 'ocupacion', label: 'Ocupación' },
        // { key: 'organizacion', label: 'Organización donde labora' },
        { key: 'tipo_lider', label: 'Tipo de líder' },
        { key: 'fuente_ingreso', label: 'Fuente de ingreso' },
        // { key: 'como_obtuvo_ingreso', label: 'Cómo obtiene ingresos' },
        { key: 'lugar_donde_labora', label: 'Lugar donde labora' },
        { key: 'eps', label: 'EPS' },
        { key: 'regimen_salud', label: 'Régimen de salud' },
        { key: 'tiene_seguro', label: 'Tiene seguro' },
        { key: 'tipo_discapacidad', label: 'Tipo de discapacidad' },
        { key: 'religion', label: 'Religión' },
        { key: 'grupo_etnico', label: 'Grupo étnico' },
        { key: 'orientacion_sexual', label: 'Orientación sexual' },
        { key: 'numero_hijos', label: 'Número de hijos' },
        { key: 'cabeza_hogar', label: 'Es cabeza de hogar' },
        { key: 'victima_conflicto_armado', label: 'Es víctima del conflicto' },
        { key: 'esquema_seguridad', label: 'Tiene esquema de seguridad' },
        { key: 'posee_tierra', label: 'Tiene tierra' },
        { key: 'area_tierra', label: 'Área del terreno' },
        { key: 'propiedad_tierra', label: 'El terreno es propio' },
        { key: 'agresor', label: 'Agresores' },
        { key: 'telefono_agresor', label: 'Teléfono del agresor' },
        { key: 'genero_agresor', label: 'Género del agresor' },
        { key: 'direccion_agresor', label: 'Dirección del agresor' },
        { key: 'relacion_con_la_victima', label: 'Relación con la víctima' },
        { key: 'grupo_responsable', label: 'Grupo presunto responsable' },
        { key: 'testigos', label: 'Testigos' },
        { key: 'activo', label: 'Estado' },
        { key: 'creado_por', label: 'Creado por' },
        { key: 'actualizado_por', label: 'Editado por' },
        { key: 'fecha_creacion', label: 'Fecha de registro' },
        { key: 'fecha_suceso', label: 'Fecha del suceso' },
    ])
    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<ExportIncidentComponent>,
        private incidentService: IncidentService,
        private toastr: ToastrService,
        private datePipe: DatePipe,
        private loadingService: LoadingService,
        private adapter: DateAdapter<Date>
    ) {
        this.adapter.setLocale('es-CO')
    }

    ngOnInit(): void {
        this.incidentService.asyncErrorValidation$.subscribe((error) => {
            if (error) {
                this.errorMsg = FormUtils.parseErrorMsgs(error)
            }
        })
    }

    drop(event: CdkDragDrop<IncidentField[]>) {
        this.fields.update((fields) => {
            moveItemInArray(fields, event.previousIndex, event.currentIndex)
            return [...fields]
        })
    }

    remove(field: IncidentField) {
        this.fields.update((fields) =>
            fields.filter((f) => f.key !== field.key)
        )
    }
    closeDialog() {
        this.dialogRef.close()
    }

    exportForm = this.fb.group({
        date_from: ['', [Validators.maxLength(11)]],
        date_to: ['', [Validators.maxLength(11)]],
        columns: [''],
    })

    convertToQueryGet(exportIncident: ExportIncident): QueryGet {
        return {
            search: '',
            limit: 0,
            offset: 0,
            order: '',
            filters: [
                { name: 'columns', value: exportIncident.columns.join(',') },
                { name: 'date_from', value: exportIncident.date_from },
                { name: 'date_to', value: exportIncident.date_to },
            ],
        }
    }

    exportIncident() {
        this.dialogRef.close()
        if (this.exportForm.valid) {
            const dateFrom = this.exportForm.get('date_from')?.value
            const dateTo = this.exportForm.get('date_to')?.value
            const payload: ExportIncident = {
                date_from: dateFrom
                    ? this.datePipe.transform(dateFrom, 'yyyy-MM-dd')!
                    : '',
                date_to: dateTo
                    ? this.datePipe.transform(dateTo, 'yyyy-MM-dd')!
                    : '',
                columns: this.fields().map((field) => field.key),
            }
            this.loadingService.show(true)
            const queryParams = this.convertToQueryGet(payload)
            this.incidentService.exportIncident(queryParams).subscribe({
                next: (res) => {
                    const response = res as AxiosResponse<Blob>
                    const data = response.data
                    const filename =
                        'content-disposition' in response.headers
                            ? response.headers['content-disposition'].split(
                                  '='
                              )[1]
                            : `suceso${payload.date_from}.xlsx`
                    this.loadingService.show(false)
                    const msg = `Archivo generado CORRECTAMENTE!`
                    this.toastr.success(msg, 'Exitoso!', {
                        progressBar: true,
                        timeOut: 5000,
                    })
                    if (data instanceof Blob || typeof data === 'string') {
                        saveAs(data, filename)
                    }
                },
                error: (err: HttpErrorResponse) => {
                    this.loadingService.show(false)

                    const backendMessage =
                        this.errorMsg ??
                        err.error?.message ??
                        'Error inesperado, por favor intentelo nuevamente.'

                    this.toastr.error('Error: ' + backendMessage, 'Error', {
                        progressBar: true,
                        timeOut: 5000,
                    })
                },
            })
        }
    }
}
